import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "../../api/client";
import { useLanguage } from "../../contexts/LanguageContext";
import logger from "../../utils/logger.js";

function getStatusMeta(statusRaw, isAr) {
  const status = (statusRaw || "").toLowerCase();

  switch (status) {
    case "completed":
      return {
        className: "badge bg-success",
        text: isAr ? "مكتملة" : "Completed",
      };
    case "processing":
    case "in_progress":
    case "paid":
      return {
        className: "badge bg-info",
        text: isAr ? "قيد المعالجة" : "Processing",
      };
    case "cancelled":
    case "canceled":
    case "refunded":
      return {
        className: "badge bg-danger",
        text: isAr ? "ملغاة" : "Canceled",
      };
    default:
      return {
        className: "badge bg-warning text-dark",
        text: isAr ? "معلقة" : "Pending",
      };
  }
}

function paymentMethodText(method, isAr) {
  const normalized = (method || "").toLowerCase();

  if (!normalized) return "—";
  if (normalized === "cod") return isAr ? "الدفع عند الاستلام" : "Cash on delivery";
  if (["card", "stripe", "stripe_card", "test_card"].includes(normalized)) {
    return isAr ? "بطاقة (Stripe)" : "Card (Stripe)";
  }
  if (normalized === "paypal") return "PayPal";
  if (normalized === "bank_transfer") return isAr ? "تحويل بنكي" : "Bank transfer";
  if (normalized === "wallet") return isAr ? "محفظة إلكترونية" : "Wallet";

  return method;
}

function invoiceTypeText(type, isAr) {
  const normalized = (type || "order").toLowerCase();

  if (normalized === "course") {
    return isAr ? "كورس" : "Course";
  }

  if (normalized === "service") {
    return isAr ? "خدمة" : "Service";
  }

  return isAr ? "طلب" : "Order";
}

function normalizeImage(rawImage) {
  const raw = (rawImage || "").trim();

  if (!raw) return "";
  if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
  if (raw.startsWith("/")) return raw;
  if (raw.startsWith("uploads/")) return `/${raw}`;

  return `/uploads/products/${raw}`;
}

export default function InvoiceDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const isAr = lang === "ar";

  const [invoice, setInvoice] = useState(null);
  const [order, setOrder] = useState(null);
  const [reference, setReference] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;

    async function loadInvoice() {
      setLoading(true);
      setError("");

      try {
        const res = await api.apiGet(`invoices/${id}`);

        if (!mounted) return;

        if (res?.status === "success") {
          setInvoice(res.invoice || null);
          setOrder(res.order || null);
          setReference(res.reference || null);
          setItems(
            Array.isArray(res.items)
              ? res.items.map((item) => ({
                  ...item,
                  name: isAr
                    ? item.name_ar || item.name_en || item.name || "Item"
                    : item.name_en || item.name_ar || item.name || "Item",
                  image: normalizeImage(item.image || item.product_image || ""),
                  price: Number(item.price || 0),
                  quantity: Number(item.quantity || 0),
                  line_total: Number(item.line_total || item.price * item.quantity || 0),
                }))
              : [],
          );
        } else {
          setError(res?.message || (isAr ? "فشل تحميل الفاتورة" : "Failed to load invoice"));
        }
      } catch (requestError) {
        logger.error("InvoiceDetails error", requestError);
        if (mounted) {
          setError(isAr ? "خطأ في الاتصال بالخادم" : "Server connection error");
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    loadInvoice();

    return () => {
      mounted = false;
    };
  }, [id, isAr]);

  const handleBack = () => {
    navigate("/profile", { replace: false });
  };

  if (loading) {
    return (
      <div className="container py-5">
        <p>{isAr ? "جارِ التحميل..." : "Loading..."}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger">{error}</div>
        <button type="button" className="btn btn-secondary" onClick={handleBack}>
          {isAr ? "رجوع" : "Back"}
        </button>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="container py-5">
        <p>{isAr ? "الفاتورة غير موجودة" : "Invoice not found"}</p>
        <button type="button" className="btn btn-secondary" onClick={handleBack}>
          {isAr ? "رجوع" : "Back"}
        </button>
      </div>
    );
  }

  const rawPdfUrl = invoice.pdf_url || "";
  const pdfUrl = rawPdfUrl.replace("https://mousagroup.nethttps://", "https://");
  const statusMeta = getStatusMeta(invoice.status, isAr);
  const invoiceDate =
    invoice.invoice_date || invoice.invoice_created_at || invoice.created_at || "";
  const orderDate = invoice.order_date || order?.created_at || invoiceDate;
  const totalAmount = Number(invoice.total || order?.total || 0);
  const shippingPrice = Number(order?.shipping_price || 0);
  const subtotal = items.reduce((sum, item) => sum + Number(item.line_total || 0), 0);
  const referenceTitle =
    reference?.title ||
    invoice.reference_title ||
    (invoice.invoice_type === "order"
      ? `#${order?.id || invoice.order_id || invoice.reference_id || ""}`
      : `#${invoice.reference_id || ""}`);

  return (
    <div
      className="container py-4 py-md-5"
      style={{
        direction: isAr ? "rtl" : "ltr",
        textAlign: isAr ? "right" : "left",
      }}
    >
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div>
          <h2 className="h4 mb-1">{isAr ? "تفاصيل الفاتورة" : "Invoice details"}</h2>
          <div className="text-muted small">
            {(isAr ? "فاتورة رقم" : "Invoice #")} {invoice.invoice_number} — {referenceTitle}
          </div>
        </div>

        <div className="d-flex gap-2 flex-wrap">
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={handleBack}>
            {isAr ? "رجوع إلى الملف الشخصي" : "Back to profile"}
          </button>

          {invoice.invoice_type === "order" && order?.id ? (
            <Link to={`/order/${order.id}`} className="btn btn-sm btn-outline-primary">
              {isAr ? "عرض الطلب" : "View order"}
            </Link>
          ) : null}

          {pdfUrl ? (
            <a href={pdfUrl} target="_blank" rel="noreferrer" className="btn btn-sm btn-success">
              {isAr ? "تحميل PDF" : "Download PDF"}
            </a>
          ) : null}
        </div>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-md-4">
          <div className="card shadow-sm h-100">
            <div className="card-header fw-bold">
              {isAr ? "معلومات الفاتورة" : "Invoice information"}
            </div>
            <div className="card-body">
              <p className="mb-1">
                <span className="text-muted small d-block">
                  {isAr ? "رقم الفاتورة" : "Invoice No."}
                </span>
                <span>{invoice.invoice_number}</span>
              </p>

              <p className="mb-1">
                <span className="text-muted small d-block">
                  {isAr ? "النوع" : "Type"}
                </span>
                <span>{invoiceTypeText(invoice.invoice_type, isAr)}</span>
              </p>

              <p className="mb-1">
                <span className="text-muted small d-block">
                  {invoice.invoice_type === "order"
                    ? isAr
                      ? "رقم الطلب"
                      : "Order ID"
                    : isAr
                      ? "المرجع"
                      : "Reference"}
                </span>
                <span>{referenceTitle}</span>
              </p>

              <p className="mb-1">
                <span className="text-muted small d-block">{isAr ? "الحالة" : "Status"}</span>
                <span className={statusMeta.className}>{statusMeta.text}</span>
              </p>

              <p className="mb-1">
                <span className="text-muted small d-block">
                  {isAr ? "طريقة الدفع" : "Payment"}
                </span>
                <span>{paymentMethodText(invoice.payment_method, isAr)}</span>
              </p>

              <p className="mb-1">
                <span className="text-muted small d-block">
                  {isAr ? "حالة الدفع" : "Payment status"}
                </span>
                <span>{invoice.payment_status || "—"}</span>
              </p>

              <p className="mb-1">
                <span className="text-muted small d-block">
                  {isAr ? "تاريخ الفاتورة" : "Invoice date"}
                </span>
                <span>{invoiceDate}</span>
              </p>

              <p className="mb-1">
                <span className="text-muted small d-block">
                  {isAr ? "تاريخ الطلب" : "Order date"}
                </span>
                <span>{orderDate || "—"}</span>
              </p>

              <p className="mt-2 fw-bold">
                {isAr ? "الإجمالي: " : "Total: "}
                {totalAmount.toFixed(2)} $
              </p>
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card shadow-sm h-100">
            <div className="card-header fw-bold">
              {invoice.invoice_type === "order"
                ? isAr
                  ? "عنوان الشحن"
                  : "Shipping address"
                : isAr
                  ? "المرجع"
                  : "Reference"}
            </div>
            <div className="card-body">
              {invoice.invoice_type === "order" ? (
                <p style={{ whiteSpace: "pre-line" }}>
                  {order?.shipping_address || (isAr ? "لا يوجد" : "No address")}
                </p>
              ) : (
                <div>
                  <p className="mb-1 fw-semibold">{referenceTitle}</p>
                  {reference?.slug ? (
                    <p className="text-muted small mb-0">{reference.slug}</p>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card shadow-sm h-100">
            <div className="card-header fw-bold">{isAr ? "ملخص" : "Summary"}</div>
            <div className="card-body">
              <p className="mb-1">
                {isAr ? "عدد العناصر: " : "Items count: "}
                <strong>{items.length}</strong>
              </p>
              <p className="mb-1">
                {isAr ? "المجموع الفرعي: " : "Subtotal: "}
                <strong>{subtotal.toFixed(2)} $</strong>
              </p>
              <p className="mb-1">
                {isAr ? "الشحن: " : "Shipping: "}
                <strong>{shippingPrice.toFixed(2)} $</strong>
              </p>
              <p className="mb-1">
                {isAr ? "الإجمالي: " : "Total: "}
                <strong>{totalAmount.toFixed(2)} $</strong>
              </p>
              <small className="text-muted">
                {pdfUrl
                  ? isAr
                    ? "تم إنشاء ملف PDF تلقائياً من هذه الفاتورة."
                    : "PDF is generated automatically from this invoice."
                  : isAr
                    ? "لم يتم إنشاء ملف PDF بعد."
                    : "PDF file not generated yet."}
              </small>
            </div>
          </div>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-header fw-bold">{isAr ? "عناصر الفاتورة" : "Invoice items"}</div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-striped mb-0 align-middle">
              <thead className="table-light">
                <tr>
                  <th style={{ width: "60px" }}>#</th>
                  <th>{isAr ? "العنصر" : "Item"}</th>
                  <th style={{ width: "110px" }}>{isAr ? "السعر" : "Price"}</th>
                  <th style={{ width: "80px" }}>{isAr ? "الكمية" : "Qty"}</th>
                  <th style={{ width: "120px" }}>{isAr ? "الإجمالي" : "Line total"}</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center text-muted py-4">
                      {isAr ? "لا توجد عناصر في هذه الفاتورة." : "No items found for this invoice."}
                    </td>
                  </tr>
                ) : (
                  items.map((item, index) => (
                    <tr key={item.id || index}>
                      <td>{index + 1}</td>
                      <td>
                        <div className="d-flex align-items-center">
                          {item.image ? (
                            <img
                              src={item.image}
                              alt={item.name || ""}
                              className="rounded border"
                              style={{
                                width: 48,
                                height: 48,
                                objectFit: "cover",
                                marginRight: isAr ? 0 : 8,
                                marginLeft: isAr ? 8 : 0,
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                width: 48,
                                height: 48,
                                background: "#eee",
                                borderRadius: 6,
                                marginRight: isAr ? 0 : 8,
                                marginLeft: isAr ? 8 : 0,
                              }}
                            />
                          )}
                          <div>
                            <div className="fw-semibold">{item.name}</div>
                            {item.color ? (
                              <div className="small text-muted">
                                {isAr ? "اللون: " : "Color: "}
                                {item.color}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </td>
                      <td>{item.price.toFixed(2)} $</td>
                      <td>{item.quantity}</td>
                      <td className="fw-semibold">{item.line_total.toFixed(2)} $</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="p-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div className="fw-semibold">
              {isAr ? "المجموع الفرعي" : "Subtotal"}: {subtotal.toFixed(2)} $
            </div>
            <button type="button" className="btn btn-secondary" onClick={handleBack}>
              {isAr ? "رجوع إلى الملف الشخصي" : "Back to profile"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
